/**
 * GDPR Service
 * Frontend service for GDPR data export and account deletion
 */

import type {
  ExportRequestResponse,
  ExportStatusResponse,
  DeletionRequestResponse,
  ExportStatus,
} from '@bat/shared'
import { apiService } from '@platform/api/api.service'

import { storageService } from '@/shared/services/storage.service'
import { ApiException } from '@/shared/utils/api'
import { API_BASE_URL } from '@/shared/utils/constants'

// ─────────────────────────────────────────────────────────────
// DATA EXPORT
// ─────────────────────────────────────────────────────────────

export interface ExportRequest {
  requestId: string
  status: ExportStatus
  format: 'json' | 'csv'
  createdAt: string
  completedAt?: string
  downloadUrl?: string
  expiresAt?: string
}

/**
 * Request a new data export
 */
export const requestDataExport = async (
  format: 'json' | 'csv' = 'json',
): Promise<ExportRequestResponse> => {
  const response = await apiService.post<ExportRequestResponse>('/api/gdpr/export', {
    format,
  })
  return response.data
}

/**
 * List all export requests for current user
 */
export const listExportRequests = async (): Promise<ExportRequest[]> => {
  const response = await apiService.get<ExportRequest[]>('/api/gdpr/exports')
  return response.data ?? []
}

/**
 * Get status of a specific export request
 */
export const getExportStatus = async (requestId: string): Promise<ExportStatusResponse> => {
  const response = await apiService.get<ExportStatusResponse>(`/api/gdpr/export/${requestId}`)
  return response.data
}

/**
 * Download a completed export file.
 *
 * The stored `downloadUrl` is a `file://` path for local storage, which a
 * browser cannot fetch from a web page. So we hit the authenticated download
 * endpoint (which streams the file) with the bearer token and trigger a blob
 * download — the api helper doesn't support blob responses, hence raw fetch.
 */
export const downloadExport = async (requestId: string, format: 'json' | 'csv'): Promise<void> => {
  const token = storageService.getAuthToken() ?? ''
  const response = await fetch(`${API_BASE_URL}/api/gdpr/export/${requestId}/download`, {
    method: 'GET',
    headers: { Authorization: token ? `Bearer ${token}` : '' },
  })
  if (!response.ok) {
    throw new ApiException({
      status: response.status,
      code: 'GDPR_EXPORT_DOWNLOAD_FAILED',
      message: '',
    })
  }
  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `data-export-${requestId}.${format}`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// ─────────────────────────────────────────────────────────────
// ACCOUNT DELETION
// ─────────────────────────────────────────────────────────────

export interface DeletionRequest {
  id: string
  status: 'pending' | 'scheduled' | 'cancelled' | 'completed'
  scheduledFor: string
  reason?: string
  createdAt: string
}

/**
 * Request account deletion with GDPR grace period
 */
export const requestAccountDeletion = async (
  confirmEmail: string,
  reason?: string,
): Promise<DeletionRequestResponse> => {
  const response = await apiService.post<DeletionRequestResponse>('/api/gdpr/deletion-request', {
    confirmEmail,
    reason,
  })
  return response.data
}

/**
 * Cancel a pending deletion request
 */
export const cancelDeletionRequest = async (
  requestId: string,
): Promise<{ success: boolean; message: string }> => {
  const response = await apiService.delete<{ success: boolean; message: string }>(
    `/api/gdpr/deletion-request/${requestId}`,
  )
  return response.data
}

/**
 * Get current deletion request status (if any)
 */
export const getDeletionStatus = async (): Promise<DeletionRequest | null> => {
  try {
    const response = await apiService.get<DeletionRequest>('/api/gdpr/deletion-status')
    return response.data ?? null
  } catch {
    // No active deletion request
    return null
  }
}
